import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut as LogOutIcon, X } from "lucide-react";

export function LogoutConfirmModal({ open, onClose, onConfirm }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[999999] flex items-center justify-center p-4 sm:p-6"
      role="presentation"
    >
      <button
        type="button"
        className="absolute inset-0 bg-[#0f172a]/55 backdrop-blur-[3px] transition-opacity"
        aria-label="Close dialog"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-desc"
        className="relative w-full max-w-[420px] rounded-2xl bg-white shadow-[0_25px_50px_-12px_rgba(15,23,42,0.25)] border border-[#E5E7EB] overflow-hidden"
      >
        <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-2">
          <div className="flex items-start gap-4 min-w-0">
            <div
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#EFF6FF] text-[#097FE6]"
              aria-hidden
            >
              <LogOutIcon className="h-6 w-6" strokeWidth={2} />
            </div>
            <div className="min-w-0 pt-0.5">
              <h2
                id="logout-dialog-title"
                className="text-lg font-semibold tracking-tight text-[#000929] sm:text-xl"
              >
                Sign out?
              </h2>
              <p
                id="logout-dialog-desc"
                className="mt-2 text-sm leading-relaxed text-[#6B7280]"
              >
                You will be signed out of your account on this device. You can
                sign in again anytime.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-2 text-[#6B7280] transition hover:bg-[#F3F4F6] hover:text-[#111827] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#097FE6] focus-visible:ring-offset-2"
            aria-label="Close"
          >
            <X className="h-5 w-5" strokeWidth={2} />
          </button>
        </div>

        <div className="flex flex-col-reverse gap-3  px-6 pb-6 pt-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-11 w-full items-center justify-center rounded-xl border border-[#E5E7EB] bg-white px-5 text-sm font-semibold text-[#374151] shadow-sm transition hover:bg-[#F9FAFB] hover:border-[#D1D5DB] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#097FE6] focus-visible:ring-offset-2 sm:w-auto sm:min-w-[112px]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="inline-flex h-11 w-full items-center justify-center rounded-xl bg-[#097FE6] px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#0866c4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#097FE6] focus-visible:ring-offset-2 sm:w-auto sm:min-w-[112px]"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

const LogOut = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);

  const handleConfirm = () => {
    localStorage.clear();
    navigate("/");
  };

  return (
    <LogoutConfirmModal
      open={open}
      onClose={() => setOpen(false)}
      onConfirm={handleConfirm}
    />
  );
};

export default LogOut;
